import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Plus, Trash2, FileDown, Calculator, Sparkles, Bot, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import MoneyInput from '../MoneyInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_OPTIONS = [
  { id: 'cap-ex', label: 'CapEx' },
  { id: 'op-ex', label: 'OpEx' },
  { id: 'ff-e', label: 'FF&E' },
  { id: 'construction', label: 'Construction' },
  { id: 'consulting', label: 'Consulting' },
  { id: 'pre-opening', label: 'Pre-Opening' },
  { id: 'forecast', label: 'Forecast' },
  { id: 'general', label: 'General' },
];

const formatMoney = (n) => {
  if (n == null || Number.isNaN(n)) return '$0';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(n);
  } catch { return `$${n}`; }
};

const computeRow = (it) => {
  const qty = parseFloat(it.quantity) || 0;
  const unit = parseFloat(it.unit_cost) || 0;
  const tax = parseFloat(it.tax_pct) || 0;
  const cont = parseFloat(it.contingency_pct) || 0;
  const subtotal = qty * unit;
  return subtotal + subtotal * (tax / 100) + subtotal * (cont / 100);
};

/* BudgetBuilderTab — the Live Budget Builder.
   Admins type rows; the per-row total + grand total recompute live.
   "Export XLSX" downloads a Hyatt-branded sheet whose totals are written
   as Excel formulas, so finance can edit downstream. */
export default function BudgetBuilderTab({ adminToken, onTotalChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  // AI prompt window state — collapsible so it doesn't crowd the table.
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiMode, setAiMode] = useState('append');
  const [aiTarget, setAiTarget] = useState('');
  const [aiBuilding, setAiBuilding] = useState(false);

  const auth = adminToken ? { headers: { Authorization: `Bearer ${adminToken}` } } : {};

  useEffect(() => {
    if (!adminToken) return;
    setLoading(true);
    axios.get(`${API}/admin/financial/line-items`, auth)
      .then(({ data }) => setRows(data.items || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  const grandTotal = rows.reduce((acc, r) => acc + computeRow(r), 0);

  // Bubble grand total up to the parent dialog so the header strip reflects edits.
  useEffect(() => {
    if (typeof onTotalChange === 'function') onTotalChange(grandTotal);
  }, [grandTotal, onTotalChange]);

  const updateCell = (idx, field, value) => {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const persist = async (next) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/financial/line-items`, { items: next }, auth);
      setRows(data.items || next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const commit = () => persist(rows);

  const addRow = () => {
    setRows((rs) => [...rs, { description: '', category: 'cap-ex', quantity: 1, unit_cost: 0, tax_pct: 0, contingency_pct: 0, notes: '' }]);
  };

  const removeRow = async (idx) => {
    const next = rows.filter((_, i) => i !== idx);
    setRows(next);
    await persist(next);
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      // Save current edits before export so the backend uses fresh data.
      await persist(rows);
      const res = await axios.post(`${API}/admin/financial/export-builder-xlsx`, {}, {
        ...auth,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `live_budget_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Live budget downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const aiBuild = async () => {
    const prompt = aiPrompt.trim();
    if (!prompt) { toast.error('Type a prompt first.'); return; }
    setAiBuilding(true);
    try {
      const body = { prompt, mode: aiMode };
      const target = parseFloat(aiTarget);
      if (Number.isFinite(target) && target > 0) body.target_total = target;
      const { data } = await axios.post(`${API}/admin/financial/builder/ai-build`, body, auth);
      setRows(data.items || []);
      setAiPrompt('');
      setAiTarget('');
      toast.success(`AI added ${data.ai_added_count} line item${data.ai_added_count === 1 ? '' : 's'} · review and edit any row inline.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI build failed');
    } finally {
      setAiBuilding(false);
    }
  };

  if (!adminToken) {
    return <div className="p-8 text-center text-sm" style={{ color: '#7A8C8C' }}>Sign in as admin to use the budget builder.</div>;
  }
  if (loading) {
    return <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline" style={{ color: '#003DA5' }} /></div>;
  }

  return (
    <div data-testid="budget-builder-tab" className="space-y-3">
      {/* AI prompt window — collapsible. Click the header to expand and
          ask GPT-5.2 to draft line items from a free-text brief. The
          generated rows land in the table immediately; admins can then
          edit any cell inline before exporting to XLSX. */}
      <div data-testid="builder-ai-panel" className="rounded-xl border overflow-hidden" style={{ borderColor: '#A3D4D4', background: 'linear-gradient(135deg, #F0FDFA 0%, #FAF8F2 100%)' }}>
        <button
          data-testid="builder-ai-toggle"
          onClick={() => setAiOpen((o) => !o)}
          className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-white/40 transition-colors"
        >
          <div className="inline-flex items-center gap-2">
            <Bot className="w-4 h-4" style={{ color: '#003DA5' }} />
            <span className="text-xs font-bold" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
              Build with AI
            </span>
            <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#003DA5', color: 'white', fontFamily: 'Manrope' }}>
              GPT-5.2
            </span>
            <span className="text-[10px]" style={{ color: '#5C6B6B', fontFamily: 'Manrope' }}>
              Type a brief — get a draft budget in seconds.
            </span>
          </div>
          <ChevronDown className={`w-4 h-4 transition-transform ${aiOpen ? 'rotate-180' : ''}`} style={{ color: '#003DA5' }} />
        </button>
        {aiOpen && (
          <div className="px-4 pb-4 space-y-3">
            <textarea
              data-testid="builder-ai-prompt"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              rows={3}
              placeholder={'e.g. "Build a CapEx budget for renovating 200 guest rooms — new beds, paint, smart lighting, mini-bars, bathroom regrout, plus 10% contingency. Around $3.2M total."'}
              className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 resize-none"
              style={{ borderColor: '#A3D4D4', backgroundColor: 'white', fontFamily: 'Manrope' }}
            />
            <div className="flex items-end gap-3 flex-wrap">
              <label className="flex flex-col gap-1">
                <span className="text-[9px] uppercase tracking-wider font-bold" style={{ color: '#003DA5' }}>Mode</span>
                <div className="inline-flex items-center rounded-lg border overflow-hidden" style={{ borderColor: '#A3D4D4' }}>
                  {[
                    { id: 'append', label: 'Add to existing' },
                    { id: 'replace', label: 'Replace all' },
                  ].map((m) => (
                    <button
                      key={m.id}
                      data-testid={`builder-ai-mode-${m.id}`}
                      onClick={() => setAiMode(m.id)}
                      className="px-3 py-1.5 text-[11px] font-bold transition-colors"
                      style={{
                        backgroundColor: aiMode === m.id ? '#003DA5' : 'white',
                        color: aiMode === m.id ? 'white' : '#003DA5',
                        fontFamily: 'Manrope',
                      }}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[9px] uppercase tracking-wider font-bold" style={{ color: '#003DA5' }}>Target $ (optional)</span>
                <MoneyInput
                  data-testid="builder-ai-target"
                  value={aiTarget}
                  onChange={(e) => setAiTarget(e.target.value)}
                  placeholder="e.g. 3,200,000"
                  className="w-40 px-3 py-1.5 rounded-lg text-xs border outline-none focus:ring-2"
                  style={{ borderColor: '#A3D4D4', backgroundColor: 'white', fontFamily: 'Manrope' }}
                />
              </label>
              <button
                data-testid="builder-ai-generate"
                onClick={aiBuild}
                disabled={aiBuilding || !aiPrompt.trim()}
                className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-40"
                style={{ background: 'linear-gradient(135deg, #003DA5 0%, #1F4FBE 100%)', fontFamily: 'Manrope' }}
              >
                {aiBuilding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {aiBuilding ? 'Drafting…' : 'Generate'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex items-center gap-2 text-xs" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
          <Calculator className="w-4 h-4" style={{ color: '#003DA5' }} />
          <span>Live formulas — edit any cell, the row total + grand total recompute instantly.</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="builder-export-xlsx"
            onClick={exportXlsx}
            disabled={exporting || rows.length === 0}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-40"
            style={{ backgroundColor: '#15803D', fontFamily: 'Manrope' }}
            title="Download a Hyatt-branded XLSX with embedded SUM formulas"
          >
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
            Export XLSX
          </button>
          <button
            data-testid="builder-add-row"
            onClick={addRow}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold text-white"
            style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope' }}
          >
            <Plus className="w-3.5 h-3.5" /> Add line item
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border" style={{ borderColor: '#E2DBC8', backgroundColor: 'white' }}>
        <table className="w-full text-sm" style={{ fontFamily: 'Manrope' }}>
          <thead style={{ backgroundColor: '#F5F0E1' }}>
            <tr style={{ borderBottom: '2px solid #003DA5' }}>
              <Th>#</Th>
              <Th className="min-w-[260px]">Description</Th>
              <Th>Category</Th>
              <Th className="text-right">Qty</Th>
              <Th className="text-right">Unit cost</Th>
              <Th className="text-right">Tax %</Th>
              <Th className="text-right">Cont. %</Th>
              <Th className="text-right">Total</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center py-10" style={{ color: '#5C6B6B' }}>
                  No line items yet. Click <strong>Add line item</strong> to start.
                </td>
              </tr>
            )}
            {rows.map((r, idx) => {
              const total = computeRow(r);
              return (
                <tr key={r.id || idx} style={{ borderBottom: '1px solid #EAE5D9' }}>
                  <td className="px-3 py-2 text-[10px] font-bold" style={{ color: '#5C6B6B' }}>{idx + 1}</td>
                  <td className="px-2 py-2">
                    <input
                      data-testid={`builder-row-desc-${idx}`}
                      value={r.description}
                      onChange={(e) => updateCell(idx, 'description', e.target.value)}
                      onBlur={commit}
                      placeholder="e.g. Lobby chandelier · 4 fixtures"
                      className="w-full px-2 py-1.5 rounded text-sm bg-transparent border border-transparent hover:border-slate-300 focus:border-blue-400 focus:bg-white outline-none"
                      style={{ color: '#1A2E2E' }}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <select
                      data-testid={`builder-row-cat-${idx}`}
                      value={r.category}
                      onChange={(e) => { updateCell(idx, 'category', e.target.value); }}
                      onBlur={commit}
                      className="px-2 py-1.5 rounded text-xs bg-transparent border border-transparent hover:border-slate-300 focus:border-blue-400 focus:bg-white outline-none"
                      style={{ color: '#1A2E2E' }}
                    >
                      {CATEGORY_OPTIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                  </td>
                  <NumCell idx={idx} field="quantity" value={r.quantity} update={updateCell} commit={commit} step="1" />
                  <NumCell idx={idx} field="unit_cost" value={r.unit_cost} update={updateCell} commit={commit} step="0.01" prefix="$" />
                  <NumCell idx={idx} field="tax_pct" value={r.tax_pct} update={updateCell} commit={commit} step="0.1" suffix="%" />
                  <NumCell idx={idx} field="contingency_pct" value={r.contingency_pct} update={updateCell} commit={commit} step="0.1" suffix="%" />
                  <td className="px-3 py-2 text-right text-sm font-bold" style={{ color: '#003DA5' }}>{formatMoney(total)}</td>
                  <td className="px-2 py-2 text-center">
                    <button data-testid={`builder-row-remove-${idx}`} onClick={() => removeRow(idx)} className="p-1 rounded hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5" style={{ color: '#DC2626' }} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: '#A3D4D440' }}>
              <td colSpan={7} className="px-3 py-3 text-right text-xs uppercase tracking-wider font-bold" style={{ color: '#0E1F2C' }}>
                Grand total
              </td>
              <td data-testid="builder-grand-total" className="px-3 py-3 text-right text-base font-black" style={{ color: '#003DA5' }}>{formatMoney(grandTotal)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="flex items-center justify-between text-[10px]" style={{ color: '#5C6B6B', fontFamily: 'Manrope' }}>
        <span>{rows.length} line item{rows.length === 1 ? '' : 's'} · per-row total = qty × unit × (1 + tax + contingency)</span>
        <span>{saving ? 'Saving…' : 'All changes saved'}</span>
      </div>
    </div>
  );
}

function Th({ children, className = '' }) {
  return (
    <th className={`px-3 py-2.5 text-left text-[9px] uppercase tracking-wider font-bold ${className}`} style={{ color: '#003DA5' }}>
      {children}
    </th>
  );
}

function NumCell({ idx, field, value, update, commit, step, prefix, suffix }) {
  // iter-120 — Money cells (prefix="$") render their committed value
  // with 2-decimal grouping (e.g. 1,000.00) so the visible amount
  // always matches the user-facing currency spec.
  const isMoney = prefix === '$';
  const displayValue = (() => {
    if (value === '' || value == null) return '';
    if (!isMoney) return value;
    const n = Number(value);
    if (!Number.isFinite(n)) return value;
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  })();
  return (
    <td className="px-1 py-2">
      <div className="flex items-center justify-end gap-0.5">
        {prefix && <span className="text-[10px]" style={{ color: '#7A8C8C' }}>{prefix}</span>}
        <input
          data-testid={`builder-row-${field}-${idx}`}
          type={isMoney ? 'text' : 'number'}
          inputMode={isMoney ? 'decimal' : undefined}
          step={isMoney ? undefined : step}
          value={displayValue}
          onChange={(e) => {
            const raw = isMoney
              ? e.target.value.replace(/[^0-9.\-]/g, '')
              : e.target.value;
            update(idx, field, raw === '' ? '' : (isMoney ? raw : parseFloat(raw)));
          }}
          onBlur={() => {
            if (isMoney && value !== '' && value != null) {
              const n = Number(String(value).replace(/[^0-9.\-]/g, ''));
              if (Number.isFinite(n)) update(idx, field, n);
            }
            commit?.();
          }}
          className="w-20 px-1.5 py-1 rounded text-xs text-right bg-transparent border border-transparent hover:border-slate-300 focus:border-blue-400 focus:bg-white outline-none"
          style={{ color: '#1A2E2E' }}
        />
        {suffix && <span className="text-[10px]" style={{ color: '#7A8C8C' }}>{suffix}</span>}
      </div>
    </td>
  );
}
